// src/components/Prospecting/TradeShows/TradeShows.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TradeShowsTable from "./TradeShowsTable";
import {
  Box,
  Typography,
  Alert,
  LinearProgress,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TradeShowSearch from "./TradeShowSearch";
import api from "../../../services/api/axios";
import BackButton from "../../../shared/components/BackButton";
import {
  downloadBlobFile,
  getTradeShowMonthAndYear,
  looksLikePlaceholderTradeShowName,
  normalizeTradeShowCountryCode,
} from "./TradeShowsHelpers";
import { ACTIONS_HEADER_BTN_SX, ACTION_MENU_PAPER_SX } from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const PAGE_BG = "#f7f9fc";


/* =========================
   HELPERS
========================= */

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

const PAGE_SIZE = 5000;

/* =========================
   COMPONENT
========================= */

export default function TradeShows() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [error, setError] = useState("");

  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedState, setSelectedState] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState([]);
  const [selectedYear, setSelectedYear] = useState([]);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  const [page, setPage] = useState(1);
  const rowsPerPage = 50;
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const navigate = useNavigate();

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  /* =========================
     DATA FETCH
  ========================= */

  const fetchPage = useCallback(async ({ searchTerm = "" }) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("top", String(PAGE_SIZE));
      if (searchTerm) params.set("search", searchTerm.trim());

      const { data } = await api.get(
        `/dynamics/tradeshows?${params.toString()}`,
      );

      setRows(Array.isArray(data?.items) ? data.items : []);
      setPage(1);
      setSelectedIds(new Set());
    } catch (e) {
      setError(
        e?.response?.data?.detail || e.message || "Failed to fetch trade shows",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage({ searchTerm: debouncedSearch });
  }, [debouncedSearch, fetchPage]);

  const onSearch = () => fetchPage({ searchTerm: search.trim() });

  const handleOpenActionsMenu = (event) => setActionsAnchorEl(event.currentTarget);
  const handleCloseActionsMenu = () => setActionsAnchorEl(null);

  const selectedCountryCodes = useMemo(
    () => selectedCountry.map(normalizeTradeShowCountryCode),
    [selectedCountry],
  );

  /* =========================
     FILTERING
  ========================= */

  const cleanRows = useMemo(() => {
    return rows.filter((r) => {
      const name = r.name;
      const start = r.start_date_raw || "";
      const country = normalizeTradeShowCountryCode(r.new_country || r.country);
      const state = r.state;
      const industry = r.industry;

      if (looksLikePlaceholderTradeShowName(name)) return false;
      if (selectedCountry.length > 0 && !selectedCountryCodes.includes(country)) return false;
      if (selectedState.length > 0 && !selectedState.includes(state)) return false;
      if (selectedIndustry.length > 0 && !selectedIndustry.includes(industry)) return false;

      if (start) {
        const { month: m, year: y } = getTradeShowMonthAndYear(start);
        if (selectedMonth.length > 0 && !selectedMonth.includes(m)) return false;
        if (selectedYear.length > 0 && !selectedYear.includes(y)) return false;
      }

      return true;
    });
  }, [rows, selectedCountry, selectedCountryCodes, selectedState, selectedIndustry, selectedMonth, selectedYear]);

  const totalPages = Math.max(1, Math.ceil(cleanRows.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = cleanRows.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage,
  );

  const currentPageIds = paginatedRows
    .map((r) => r?.id)
    .filter((id) => id != null && id !== "");

  const allCurrentSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.has(id));
  const someCurrentSelected = currentPageIds.some((id) => selectedIds.has(id));

  const handleToggleOne = (id) => {
    if (id == null || id === "") return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleTogglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) currentPageIds.forEach((id) => next.delete(id));
      else currentPageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /* =========================
     EXPORT
  ========================= */

  const handleExport = async () => {
    try {
      const response = await api.get("/dynamics/tradeshows/export", {
        params: {
          search: search || undefined,
          country: selectedCountry.length ? selectedCountryCodes.join(",") : undefined,
          state: selectedState.length ? selectedState.join(",") : undefined,
          industry: selectedIndustry.length ? selectedIndustry.join(",") : undefined,
          month: selectedMonth.length ? selectedMonth.join(",") : undefined,
          year: selectedYear.length ? selectedYear.join(",") : undefined,
        },
        responseType: "blob",
      });
      downloadBlobFile(new Blob([response.data]), "TradeShows.xlsx");
    } catch {
      setError("Export failed");
    }
  };

  /* =========================
     SAVE TO MY TRADE SHOWS
  ========================= */

  const handleSaveSelected = async () => {
    const items = cleanRows
      .filter((r) => r?.id != null && selectedIds.has(r.id))
      .map((r) => ({
        dynamics_id: r.id,
        name: r.name,
        start_date: r.start_date_raw || null,
        end_date: r.end_date_raw || null,
        city: r.city || null,
        state: r.state || null,
        country: r.country || null,
        industry: r.industry || null,
        website: r.website || null,
      }));

    if (items.length === 0) {
      setError("No rows selected to save.");
      return;
    }

    try {
      await api.post("/user-trade-shows", { items });
      setSelectedIds(new Set());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save trade shows");
    }
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <BackButton to="/tradeshows" label="Back" />
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "#648315",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <EventIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_BLUE,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Trade Shows
            </Typography>
          </Box>

          {/* Center: search */}
          <Box sx={{ flexGrow: 1, minWidth: 220, width: { xs: "100%", md: "auto" } }}>
            <TradeShowSearch value={search} onChange={setSearch} onEnter={onSearch} />
          </Box>

          {/* Right: actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => { handleCloseActionsMenu(); handleSaveSelected(); }}
                disabled={selectedIds.size === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save to My Trade Shows</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => { handleCloseActionsMenu(); navigate("/my-trade-shows"); }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_BLUE }}>
                  <EventAvailableIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>My Trade Shows</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => { handleCloseActionsMenu(); handleExport(); }}
                disabled={cleanRows.length === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0284C7" }}>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {loading && <LinearProgress />}
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TradeShowsTable
          rows={rows}
          paginatedRows={paginatedRows}
          cleanRows={cleanRows}
          loading={loading}
          selectedIds={selectedIds}
          handleToggleOne={handleToggleOne}
          handleTogglePage={handleTogglePage}
          allCurrentSelected={allCurrentSelected}
          someCurrentSelected={someCurrentSelected}
          currentPageIds={currentPageIds}
          page={safePage}
          totalPages={totalPages}
          setPage={setPage}
          selectedCountry={selectedCountry}
          onCountryChange={(val) => { setSelectedCountry(val); setSelectedState([]); }}
          selectedState={selectedState}
          onStateChange={setSelectedState}
          selectedIndustry={selectedIndustry}
          onIndustryChange={setSelectedIndustry}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
      </Box>
    </Box>
  );
}
